import {BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {IApp, IAppCtx} from "back/core/appFrame";

import {ENodeType, JSX} from "lib/commons/xml/dom";
import {IView, IViewsContainer, OViewVisitOptions} from "lib/commons/views";
import {IReg, REG} from "lib/commons/registry";
import {DOMSH} from "lib/commons/xml/domsh";
import {JLogsAppDef} from "back/core/plugins/logsPlg";
import {BasicUniverse, IUniverseEnv} from "lib/core/universe";
import {ILastDatasBuilder, JLastDatas, LASTDATAS} from "lib/commons/lastDatas";
import {BlocksCollapsable, JLDBlocksCollapsable, OBlocksCollapsableInit} from "back/commons/widgets/blocks";
import {Area} from "lib/commons/areas";
import {AppHeader} from "back/core/widgets/appHeader";
import "back/core/apps/apps_Perms";


interface JLogsAppEntry {
	universe: BasicUniverse
	name?: string
}

export interface LogsApp extends BaseElementAsync {
	initialize(init: OLogsAppInit & IAppCtx<IUniverseEnv>): this;
}

export interface OLogsAppInit extends OSkinableInit {
	/** nombre d'entrées de log maximal ouvert par défaut.
	 * 5 si non défini */
	maxOpenEntriesByDefault?: number,
	blocksCollapsableInit?: OBlocksCollapsableInit<any>,
}

export class LogsApp extends BaseElementAsync implements IApp<IUniverseEnv>, IViewsContainer, ILastDatasBuilder {

	reg: IReg<IUniverseEnv>;

	appDef: JLogsAppDef;

	appHeader: AppHeader<LogsApp>;

	protected _blocksCollapsable: BlocksCollapsable;

	protected async _initialize(init: OLogsAppInit & IAppCtx<IUniverseEnv>) {
		this.appDef = init.appDef as JLogsAppDef;
		this.reg = this.findReg(init) as IReg<IUniverseEnv>;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		const sr = this.shadowRoot;
		const showUniverses = this.appDef.logsUnivers;

		if (!init.maxOpenEntriesByDefault) init.maxOpenEntriesByDefault = 5;

		//AppHeader
		this.appHeader = sr.appendChild(new AppHeader<LogsApp>().initialize({
			reg: this.reg,
			actionContext: this,
		}));

		this.setAttribute("label", "Logs système");
		if (!init.hasHeadTitleBar) this.appHeader.appendChild(<header><h1>Logs système</h1></header>);

		let logsAppEntries = init.reg.getList("plg:logs:entries") as JLogsAppEntry[];
		logsAppEntries = logsAppEntries.filter((entry) => LogsApp.canReadLogUniverce(entry.universe));

		const LDkey = "blocks";
		let LD: JLDBlocksCollapsable = (init.lastDatas ? init.lastDatas[LDkey] : null) as JLDBlocksCollapsable;

		// Ouverture des blocks par défaut (si pas de LD)
		if (!LD && init.maxOpenEntriesByDefault) {
			LD = {open: []};
			for (var i = 0; i < logsAppEntries.length; i++) {
				const logAppEntry = logsAppEntries[i];
				if (i < init.maxOpenEntriesByDefault)
					LD.open.push(logAppEntry.universe.getId())
				else
					break;
			}
		}

		this._blocksCollapsable = sr.appendChild(<BlocksCollapsable î={Object.assign({
			reg: this.reg,
			areasContext: {reg: this.reg},
			skinOver: "logs-app/collapsableBlocks",
			lastDatasKey: LDkey,
			lastDatas: LD,
		} as OBlocksCollapsableInit<any>, init.blocksCollapsableInit)}/>) as BlocksCollapsable;

		logsAppEntries.forEach((logAppEntry, pos) => {
			this._blocksCollapsable.addArea(
				new Area(logAppEntry.universe.getId()).setLabel(logAppEntry.name || logAppEntry.universe.getName()).requireVisiblePerm("ui.apps.logs.show").setBodyBuilder((ctx) => {
						let iframe = <iframe src={logAppEntry.universe.config.adminLogsUrl}/> as HTMLIFrameElement;
						iframe.addEventListener("load", () => {
							this.forceOpenLinksInNewTab(iframe, this.reg);
						});
						return iframe;
					}
				),
				null,
				{
					reg: logAppEntry.universe.reg,
					areaContext: {reg: logAppEntry.universe.reg},
					parentBlockCtn: this._blocksCollapsable,
					locked: logsAppEntries.length == 1 ? true : undefined,
				}
			)
		});
	}

	updateAppDef(def: JLogsAppDef): boolean {
		if (this.appDef === def) return true; //chgt issu d'ici
		return false;
	}

	buildLastDatas(parentLastDatas: JLastDatas): void {
		LASTDATAS.buildLastDatas(parentLastDatas, this._blocksCollapsable, true);
	}

	visitViews(visitor: (view: IView) => any) {
		visitor(this._blocksCollapsable)
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return visitor(this._blocksCollapsable);
	}

	/** */
	private forceOpenLinksInNewTab(frame: HTMLIFrameElement, reg: IReg<any>) {
		//note : frame.contentDocument null si pas "sameSite" (sécurité navigateurs).
		frame.contentDocument?.addEventListener('click', (ev) => {
			if (ev.defaultPrevented) return;

			const target = ev.target as Element;
			let link: Element = null;
			if (target.nodeName == "a") link = target;
			if (!link) {
				// Typage volontairement incorrect : targetPaths contient document et window, qui ne passeront pas la première condition
				const targetPaths = ev.composedPath() as Element[];
				for (let i = 1; i < targetPaths.length; i++) {
					const target = targetPaths[i];
					if (target.nodeType != ENodeType.element) break;
					else if (target.nodeName == "a") {
						link = target;
						break;
					}
				}
			}
			if (!link) return;
			link.setAttribute("target", "_blank");
		});
	}

	/** Retourne true si au moins un univers est visible dans cette app */
	static atLeastOneEntry(reg: IReg<any>): boolean {
		let logsAppEntries = reg.getList("plg:logs:entries") as JLogsAppEntry[] || [];
		let result = false;
		for (const logAppEntry of logsAppEntries) {
			if (this.canReadLogUniverce(logAppEntry.universe)) {
				result = true;
				break;
			}
		}
		return result;
	}

	/** Retourne true si cet univers est visible dans cette app */
	private static canReadLogUniverce(univ: BasicUniverse): boolean {
		return univ.config.adminLogsUrl && univ.reg.hasPerm("ui.apps.logs.show") ? true : false;
	}
}


customElements.define('logs-app', LogsApp);
REG.reg.registerSkin('logs-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	header {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		max-height: 5em;
		overflow: hidden;
		align-items: center;
		justify-content: center;
		user-select: none;
	}

	h1 {
		margin: .1em .5em;
		font-size: 1em;
		letter-spacing: 0.1em;
		text-align: center;
	}

	c-blocks-collapsable {
		flex: 1;
		min-height: 0;
		min-width: 0;
	}
`);


REG.reg.registerSkin('logs-app/collapsableBlocks', 1, /* language=CSS */ `
	iframe {
		width: 100%;
		height: 100%;
		border: none;
		background-color: var(--form-bgcolor);
	}
`);
